import React, { useState, useEffect } from 'react';
import Card from "components/card";
import { 
  MdInventory,
  MdLocationOn,
  MdTimer,
  MdSearch,
  MdFilterList,
  MdInfo,
  MdWarning,
  MdTrendingUp,
  MdCalendarToday,
  MdViewModule,
  MdGridOn,
  MdBuild,
  MdClose,
  MdError,
  MdCheckCircle,
  MdGetApp
} from "react-icons/md";
import axios from 'axios';
import UsibrasLoader from '../../../components/UsibrasLoader';
import UsibrasToast from '../../../components/UsibrasToast';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import logo from 'assets/img/auth/usibras_ghana_limited_logo.jpeg';

const Age = () => {
  const [products, setProducts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLane, setSelectedLane] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [sortBy, setSortBy] = useState('age');
  const [showReworkModal, setShowReworkModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedAction, setSelectedAction] = useState('');
  const [selectedUnit, setSelectedUnit] = useState('');
  const [reworkQuantity, setReworkQuantity] = useState('');
  const [quantityError, setQuantityError] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reworkNote, setReworkNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'info' });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  // Factory units for rework
  const factoryUnits = [
    'Piecies Line',
    'Wholes Line',
    'Manual Peeling',
    'Manual Polishing',
    'Packaging Unit'
  ];

  // Fetch data from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        const response = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
        if (response.data && response.data.data) {
          // Flatten all batch details into a single array
          const allBatches = response.data.data.flatMap(product => 
            product.batch_details.map(batch => ({
              ...batch,
              productType: product.product_type
            }))
          );
          setProducts(allBatches);
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('Error:', err);
        setError('Error loading products');
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchProducts();
  }, []);

  // Calculate statistics
  const calculateStats = (products) => {
    const totalProducts = products.length;
    const totalQuantity = products.reduce((sum, p) => sum + parseInt(p.batch_actual_stock), 0);
    const avgAge = Math.round(products.reduce((sum, p) => sum + Math.abs(p.age), 0) / totalProducts);
    const oldestProduct = Math.max(...products.map(p => Math.abs(p.age)));

    return {
      totalProducts,
      totalQuantity,
      avgAge,
      oldestProduct
    };
  };

  // Filter and sort products
  const filteredProducts = products
    .filter(product => {
      const matchesSearch = 
        product.productType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.batch_id.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesLane = !selectedLane || (product.lane || `Lane ${product.zone}`) === selectedLane;
      const matchesSlot = !selectedSlot || (product.slot || product.rack).toString() === selectedSlot;
      return matchesSearch && matchesLane && matchesSlot;
    })
    .sort((a, b) => {
      if (sortBy === 'age') return Math.abs(b.age) - Math.abs(a.age);
      if (sortBy === 'quantity') return parseInt(b.batch_actual_stock) - parseInt(a.batch_actual_stock);
      if (sortBy === 'status') {
        return a.status.localeCompare(b.status);
      }
      return 0;
    });

  const stats = calculateStats(filteredProducts);

  // Get unique lanes and slots for filters
  const lanes = [...new Set(products.map(p => p.lane || `Lane ${p.zone}`))];
  const slots = [...new Set(products.map(p => p.slot || p.rack))];

  // Pagination
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handleReworkClick = (product, action) => {
    setSelectedProduct(product);
    setSelectedAction(action);
    setReworkQuantity(product.batch_actual_stock.toString());
    setQuantityError('');
    setShowReworkModal(true);
  };

  const handleConfirmRework = async () => {
    // Validate quantity
    const quantity = parseInt(reworkQuantity);
    if (!quantity || quantity <= 0) {
      setQuantityError('Please enter a valid quantity');
      return;
    }
    if (quantity > parseInt(selectedProduct.batch_actual_stock)) {
      setQuantityError(`Quantity cannot exceed available amount (${selectedProduct.batch_actual_stock} boxes)`);
      return;
    }

    try {
      setIsSubmitting(true);
      const reworkData = {
        batch_id: selectedProduct.batch_id,
        product_type: selectedProduct.productType,
        quantity: quantity,
        lane: selectedProduct.lane || `Lane ${selectedProduct.zone}`,
        slot: selectedProduct.slot || selectedProduct.rack,
        storage_date: selectedProduct.date,
        age_days: Math.abs(selectedProduct.age),
        factory_unit: selectedUnit,
        type: selectedAction,
        notes: reworkNote
      };

      const response = await axios.post('https://usibras-web-backend.onrender.com/api/rework', reworkData);
      
      if (response.data.message === "Rework record added successfully") {
        setToast({
          show: true,
          message: response.data.message,
          type: 'success'
        });

        // Close modal and reset states
        setShowReworkModal(false);
        setSelectedProduct(null);
        setSelectedUnit('');
        setReworkNote('');
        setReworkQuantity('');
        setQuantityError('');
        
        // Refresh the products list
        const updatedResponse = await axios.get('https://usibras-web-backend.onrender.com/api/fetch-finished-products');
        if (updatedResponse.data && updatedResponse.data.data) {
          const allBatches = updatedResponse.data.data.flatMap(product => 
            product.batch_details.map(batch => ({
              ...batch,
              productType: product.product_type
            }))
          );
          setProducts(allBatches);
        }
      }
    } catch (error) {
      console.error('Error submitting rework:', error);
      setToast({
        show: true,
        message: error.response?.data?.message || 'Failed to process rework request',
        type: 'error'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Download PDF function
  const downloadPdf = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    let y = 10;

    // Logo
    const img = new window.Image();
    img.src = logo;
    doc.addImage(img, 'JPEG', 10, y, 30, 30);

    // Title and Date
    doc.setFontSize(18);
    doc.text('Product Age Report', pageWidth / 2, y + 10, { align: 'center' });
    doc.setFontSize(12);
    doc.text('Product Age and Location Summary', pageWidth / 2, y + 18, { align: 'center' });
    doc.setFontSize(10);
    doc.text(`Generated: ${new Date().toLocaleDateString('en-CA')}`, pageWidth / 2, y + 26, { align: 'center' });

    // Summary information
    doc.setFontSize(12);
    doc.text(`Total Products: ${stats.totalQuantity.toLocaleString()} boxes`, 14, y + 40);
    doc.text(`Total Batches: ${stats.totalProducts}`, 14, y + 48);
    doc.text(`Average Age: ${stats.avgAge} days`, 14, y + 56);
    doc.text(`Oldest Product: ${stats.oldestProduct} days`, 14, y + 64);
    doc.text(`Batches > 6 Months: ${filteredProducts.filter(p => Math.abs(p.age) > 180).length}`, 14, y + 72);

    // Products table
    autoTable(doc, {
      startY: y + 85,
      head: [['Batch ID', 'Product Type', 'Quantity', 'Location', 'Storage Date', 'Age (days)']],
      body: filteredProducts.map(item => [
        item.batch_id,
        item.productType,
        `${item.batch_actual_stock} boxes`,
        `${item.lane || `Lane ${item.zone}`}, Slot ${item.slot || item.rack}`,
        new Date(item.date).toLocaleDateString(),
        Math.abs(item.age)
      ]),
      headStyles: { fillColor: [22, 160, 133] },
      theme: 'grid',
      styles: {
        fontSize: 8,
        cellPadding: 3,
      },
      columnStyles: {
        0: { cellWidth: 25 },
        1: { cellWidth: 35 },
        2: { cellWidth: 20 },
        3: { cellWidth: 35 },
        4: { cellWidth: 25 },
        5: { cellWidth: 20 }
      }
    });

    // Add summary at the bottom
    const finalY = doc.lastAutoTable.finalY + 10;
    doc.setFontSize(10);
    doc.text(`Report generated on: ${new Date().toLocaleString()}`, 14, finalY);
    doc.text(`Data source: Product Age Dashboard`, 14, finalY + 8);

    doc.save('product-age-report.pdf');
  };

  if (loading) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="relative">
          <div className="h-24 w-24 rounded-full border-t-4 border-b-4 border-brand-500"></div>
          <div className="absolute top-0 left-0 h-24 w-24 animate-spin rounded-full border-t-4 border-b-4 border-gray-200"></div>
          <div className="absolute top-1/2 left-1/2 h-4 w-4 -translate-x-1/2 -translate-y-1/2 rounded-full bg-brand-500"></div>
          <div className="mt-8 text-center">
            <p className="text-lg font-medium text-gray-600 dark:text-white">Loading</p>
            <p className="text-sm text-gray-400">Please wait...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-[400px] items-center justify-center">
        <div className="text-center">
          <div className="mb-4 text-5xl text-red-500">
            <MdError className="mx-auto h-16 w-16" />
          </div>
          <p className="text-xl font-semibold text-gray-700 dark:text-white">
            Oops! Something went wrong
          </p>
          <p className="mt-2 text-gray-500 dark:text-gray-400">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mt-5 h-full">
      {/* Toast Notification */}
      {toast.show && (
        <UsibrasToast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast(prev => ({ ...prev, show: false }))}
          position="top-right"
          duration={3000}
        />
      )}

      <Card extra={"w-full h-full p-4"}>
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-navy-700 dark:text-white">
            Product Age Dashboard
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            Monitor product age and location in the warehouse
          </p>
        </div>

        {/* Summary Cards */}
        <div className="mb-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-blue-50 p-3 dark:bg-navy-700">
                <MdInventory className="h-6 w-6 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Total Products</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.totalQuantity.toLocaleString()} boxes</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-green-50 p-3 dark:bg-navy-700">
                <MdTimer className="h-6 w-6 text-green-600 dark:text-green-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Average Age</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.avgAge} days</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-purple-50 p-3 dark:bg-navy-700">
                <MdTrendingUp className="h-6 w-6 text-purple-600 dark:text-purple-400" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Oldest Product</p>
                <p className="text-xl font-bold text-navy-700 dark:text-white">{stats.oldestProduct} days</p>
              </div>
            </div>
          </Card>

          <Card extra="p-4">
            <div className="flex items-center gap-4">
              <div className="rounded-full bg-red-50 p-3 dark:bg-navy-700">
                <MdWarning className="h-6 w-6 text-red-600 dark:text-red-400" />
              </div>
                             <div>
                 <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Batches More Than 6 Months</p>
                 <p className="text-xl font-bold text-navy-700 dark:text-white">
                   {filteredProducts.filter(p => Math.abs(p.age) > 180).length}
                 </p>
               </div>
            </div>
          </Card>
        </div>

        {/* Filters and Download */}
        <div className="mb-8 rounded-xl border border-gray-200 bg-white p-4 dark:border-navy-700 dark:bg-navy-800">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5">
            <div className="flex items-center gap-2">
              <MdSearch className="h-5 w-5 text-gray-500" />
              <input
                type="text"
                placeholder="Search products..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              />
            </div>
            <div className="flex items-center gap-2">
              <MdGridOn className="h-5 w-5 text-gray-500" />
              <select
                value={selectedLane}
                onChange={(e) => setSelectedLane(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Lanes</option>
                {lanes.map(lane => (
                  <option key={lane} value={lane}>{lane}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <MdViewModule className="h-5 w-5 text-gray-500" />
              <select
                value={selectedSlot}
                onChange={(e) => setSelectedSlot(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="">All Slots</option>
                {slots.map(slot => (
                  <option key={slot} value={slot}>Slot {slot}</option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-2">
              <MdFilterList className="h-5 w-5 text-gray-500" />
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm dark:border-navy-600 dark:bg-navy-700"
              >
                <option value="age">Sort by Age</option>
                <option value="quantity">Sort by Quantity</option>
              </select>
            </div>
            <div className="flex items-center justify-end">
              <button
                onClick={downloadPdf}
                className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors duration-200"
                disabled={loading || filteredProducts.length === 0}
              >
                <MdGetApp className="mr-2" size={20} />
                <span>Download PDF</span>
              </button>
            </div>
          </div>
        </div>

        {/* Products Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200 dark:border-navy-700">
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">ID</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Product Type</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Batch Stock</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Location</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Date of Storage</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Age (days)</th>
                <th className="pb-3 text-left text-sm font-medium text-gray-600 dark:text-gray-400">Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedProducts.map((product) => (
                <tr key={product.id} className="border-b border-gray-200 dark:border-navy-700">
                  <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                    {product.batch_id}
                  </td>
                  <td className="py-4 text-sm font-medium text-navy-700 dark:text-white">
                    {product.productType}
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdInventory className="h-4 w-4 text-green-500" />
                      <span>{product.batch_actual_stock} boxes</span>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdLocationOn className="h-4 w-4 text-blue-500" />
                      <span>{product.lane || `Lane ${product.zone}`}, Slot {product.slot || product.rack}</span>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdCalendarToday className="h-4 w-4 text-purple-500" />
                      <span>{new Date(product.date).toLocaleDateString()}</span>
                    </div>
                  </td>
                  <td className="py-4 text-sm text-gray-600 dark:text-gray-400">
                    <div className="flex items-center gap-2">
                      <MdTimer className="h-4 w-4 text-purple-500" />
                      <span>{Math.abs(product.age)} days</span>
                    </div>
                  </td>
                  <td className="py-4">
                    <div className="flex flex-col gap-2">
                      <button
                        onClick={() => handleReworkClick(product, 'Rework')}
                        className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                          product.status === 'Needs Rework'
                            ? 'bg-red-500 hover:bg-red-600 focus:ring-red-500 dark:bg-red-600 dark:hover:bg-red-700'
                            : product.status === 'Approaching 6 Months'
                            ? 'bg-yellow-500 hover:bg-yellow-600 focus:ring-yellow-500 dark:bg-yellow-600 dark:hover:bg-yellow-700'
                            : 'bg-green-500 hover:bg-green-600 focus:ring-green-500 dark:bg-green-600 dark:hover:bg-green-700'
                        }`}
                      >
                        <MdBuild className="h-4 w-4" />
                        Rework
                      </button>
                      <button
                        onClick={() => handleReworkClick(product, 'Reprocess')}
                        className="inline-flex items-center gap-2 rounded-lg bg-blue-500 px-3 py-2 text-sm font-medium text-white hover:bg-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:bg-blue-600 dark:hover:bg-blue-700"
                      >
                        <MdBuild className="h-4 w-4" />
                        Reprocess
                      </button>
                      <button
                        onClick={() => handleReworkClick(product, 'Sampling')}
                        className="inline-flex items-center gap-2 rounded-lg bg-purple-500 px-3 py-2 text-sm font-medium text-white hover:bg-purple-600 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 dark:bg-purple-600 dark:hover:bg-purple-700"
                      >
                        <MdBuild className="h-4 w-4" />
                        Sampling
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Rework Confirmation Modal */}
        {showReworkModal && selectedProduct && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-2 sm:p-4">
            <div 
              className="absolute inset-0 bg-black bg-opacity-50 backdrop-blur-sm transition-opacity duration-300 ease-in-out"
              onClick={() => setShowReworkModal(false)}
            />
            
            <div className="relative w-full max-w-md max-h-[90vh] overflow-y-auto transform rounded-xl bg-white p-3 shadow-2xl transition-all duration-300 ease-in-out dark:bg-navy-800 sm:p-6">
              <div className="mb-4 sm:mb-6 flex items-center justify-between border-b border-gray-200 pb-3 sm:pb-4 dark:border-navy-700">
                <div className="flex items-center gap-2 sm:gap-3">
                  <div className="rounded-full bg-red-100 p-1.5 sm:p-2 dark:bg-red-900">
                    <MdBuild className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-base sm:text-lg font-bold text-navy-700 dark:text-white">
                    Confirm {selectedAction}
                  </h3>
                </div>
                <button
                  onClick={() => setShowReworkModal(false)}
                  className="rounded-full p-1 sm:p-1.5 text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700 dark:hover:bg-navy-700 dark:hover:text-gray-300"
                >
                  <MdClose className="h-4 w-4 sm:h-5 sm:w-5" />
                </button>
              </div>

              <div className="mb-4 sm:mb-6 space-y-3 sm:space-y-4">
                <div className="grid grid-cols-1 gap-2 sm:gap-3 sm:grid-cols-2">
                  <div className="rounded-lg bg-gray-50 p-2 sm:p-3 dark:bg-navy-700">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Product ID</span>
                    <span className="mt-1 block text-xs sm:text-sm font-semibold text-navy-700 dark:text-white break-words">{selectedProduct.batch_id}</span>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-2 sm:p-3 dark:bg-navy-700">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Product Type</span>
                    <span className="mt-1 block text-xs sm:text-sm font-semibold text-navy-700 dark:text-white break-words">{selectedProduct.productType}</span>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-2 sm:p-3 dark:bg-navy-700">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Available Quantity</span>
                    <span className="mt-1 block text-xs sm:text-sm font-semibold text-navy-700 dark:text-white">{selectedProduct.batch_actual_stock} boxes</span>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-2 sm:p-3 dark:bg-navy-700">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Location</span>
                    <span className="mt-1 block text-xs sm:text-sm font-semibold text-navy-700 dark:text-white break-words">
                      {selectedProduct.lane || `Lane ${selectedProduct.zone}`}, Slot {selectedProduct.slot || selectedProduct.rack}
                    </span>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-2 sm:p-3 dark:bg-navy-700">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Storage Date</span>
                    <span className="mt-1 block text-xs sm:text-sm font-semibold text-navy-700 dark:text-white">
                      {new Date(selectedProduct.date).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="rounded-lg bg-gray-50 p-2 sm:p-3 dark:bg-navy-700">
                    <span className="block text-xs font-medium text-gray-500 dark:text-gray-400">Age</span>
                    <span className="mt-1 block text-xs sm:text-sm font-semibold text-navy-700 dark:text-white">{Math.abs(selectedProduct.age)} days</span>
                  </div>
                </div>

                <div className="mt-3 sm:mt-4">
                  <label className="mb-1 sm:mb-2 block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                    Rework Quantity
                  </label>
                  <input
                    type="number"
                    value={reworkQuantity}
                    onChange={(e) => {
                      setReworkQuantity(e.target.value);
                      setQuantityError('');
                    }}
                    min="1"
                    max={selectedProduct.batch_actual_stock}
                    className={`w-full rounded-lg border px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:outline-none focus:ring-1 ${
                      quantityError 
                        ? 'border-red-300 focus:border-red-500 focus:ring-red-500' 
                        : 'border-gray-200 focus:border-blue-500 focus:ring-blue-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white'
                    }`}
                    placeholder="Enter quantity to rework"
                  />
                  {quantityError && (
                    <p className="mt-1 text-xs sm:text-sm text-red-600 dark:text-red-400">{quantityError}</p>
                  )}
                </div>

                <div className="mt-3 sm:mt-4">
                  <label className="mb-1 sm:mb-2 block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                    Select Factory Unit for Rework
                  </label>
                  <select
                    value={selectedUnit}
                    onChange={(e) => setSelectedUnit(e.target.value)}
                    className="w-full rounded-lg border border-gray-200 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                  >
                    <option value="">Select a unit...</option>
                    {factoryUnits.map((unit) => (
                      <option key={unit} value={unit}>
                        {unit}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="mt-3 sm:mt-4">
                  <label className="mb-1 sm:mb-2 block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
                    Add Note (Optional)
                  </label>
                  <textarea
                    value={reworkNote}
                    onChange={(e) => setReworkNote(e.target.value)}
                    placeholder="Enter any additional notes about the rework process..."
                    className="w-full rounded-lg border border-gray-200 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 dark:border-navy-600 dark:bg-navy-700 dark:text-white"
                    rows="2"
                  />
                </div>
              </div>

              <div className="mb-4 sm:mb-6 rounded-lg bg-red-50 p-2 sm:p-3 dark:bg-red-900/30">
                <div className="flex items-start gap-2 sm:gap-3">
                  <MdWarning className="mt-0.5 h-4 w-4 sm:h-5 sm:w-5 text-red-600 dark:text-red-400 flex-shrink-0" />
                  <div>
                    <h4 className="text-xs sm:text-sm font-medium text-red-800 dark:text-red-200">Rework Required</h4>
                    <p className="mt-1 text-xs sm:text-sm text-red-700 dark:text-red-300">
                      This product has exceeded the recommended storage period and requires rework.
                    </p>
                  </div>
                </div>
              </div>

              <div className="flex flex-col gap-2 sm:gap-3 sm:flex-row sm:justify-end">
                <button
                  onClick={() => setShowReworkModal(false)}
                  disabled={isSubmitting}
                  className="rounded-lg border border-gray-300 px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 dark:border-navy-600 dark:text-gray-300 dark:hover:bg-navy-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Cancel
                </button>
                <button
                  onClick={handleConfirmRework}
                  disabled={!selectedUnit || !reworkQuantity || isSubmitting}
                  className={`relative rounded-lg px-3 sm:px-4 py-2 sm:py-2.5 text-xs sm:text-sm font-medium text-white transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 ${
                    selectedUnit && reworkQuantity && !isSubmitting
                      ? 'bg-red-500 hover:bg-red-600 focus:ring-red-500 dark:bg-red-600 dark:hover:bg-red-700'
                      : 'cursor-not-allowed bg-gray-400 dark:bg-gray-600'
                  }`}
                >
                  {isSubmitting ? (
                    <UsibrasLoader size="small" color="white" />
                  ) : (
                    'Confirm'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Pagination */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm font-medium text-navy-700 dark:text-white">
            Showing {paginatedProducts.length > 0 ? (currentPage - 1) * itemsPerPage + 1 : 0} to {Math.min(currentPage * itemsPerPage, filteredProducts.length)} of {filteredProducts.length} entries
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage(1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              First
            </button>
            <button
              onClick={() => setCurrentPage(currentPage - 1)}
              disabled={currentPage === 1}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === 1
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Previous
            </button>
            
            {/* Page number buttons */}
            {[...Array(Math.min(5, totalPages))].map((_, index) => {
              let pageNum;
              if (totalPages <= 5) {
                pageNum = index + 1;
              } else if (currentPage <= 3) {
                pageNum = index + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNum = totalPages - 4 + index;
              } else {
                pageNum = currentPage - 2 + index;
              }
              
              return (
                <button
                  key={index}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                    currentPage === pageNum
                      ? "bg-brand-700 text-white"
                      : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            
            <button
              onClick={() => setCurrentPage(currentPage + 1)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Next
            </button>
            <button
              onClick={() => setCurrentPage(totalPages)}
              disabled={currentPage === totalPages || totalPages === 0}
              className={`linear rounded-md px-3 py-1 text-sm font-medium transition duration-200 ${
                currentPage === totalPages || totalPages === 0
                  ? "cursor-not-allowed bg-gray-300 text-gray-500"
                  : "bg-brand-500 text-white hover:bg-brand-600 active:bg-brand-700"
              }`}
            >
              Last
            </button>
          </div>
        </div>

        {/* No Results Message */}
        {filteredProducts.length === 0 && (
          <div className="mt-8 text-center">
            <MdInfo className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-lg font-medium text-gray-600 dark:text-gray-400">
              No products found matching your criteria
            </p>
          </div>
        )}
      </Card>
    </div>
  );
};

export default Age; 